import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNavBadges } from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * HubNavPanel — sidebar for the four consolidated hubs (Studio, Insights,
 * Knowledge Hub, Compliance Reference). Mirrors WorkspaceNavPanel's structure
 * so entering a hub feels the same as entering a client workspace:
 *
 *   ┌─────────────────┐
 *   │ ← BACK          │   → navigate to /  (Command Center / main menu)
 *   ├─────────────────┤
 *   │ HUB TITLE       │   → title + eyebrow
 *   ├─────────────────┤
 *   │ SECTION LABEL   │
 *   │  · Item 1       │
 *   │  · Item 2 (on)  │
 *   │  · Item 3       │
 *   └─────────────────┘
 *
 * Collapsed mode collapses to an icon rail — same interaction shape as the
 * global sidebar's collapsed variant.
 */
function ItemBadge({ count, tone }) {
  if (!count) return null;
  const display = count > 99 ? "99+" : String(count);
  return (
    <span
      className={cn(
        "ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-[2px] px-1 font-mono text-[9px] font-bold leading-none tabular-nums",
        tone === "attention" ? "bg-destructive text-white" : "border border-border bg-secondary text-muted-foreground"
      )}
    >
      {display}
    </span>
  );
}

export function HubNavPanel({ hub, collapsed = false, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const { data: badges } = useNavBadges();

  const activeKey = hub.activeItemKey ? hub.activeItemKey(location.pathname, location.search) : null;
  const goBack = () => {
    // Always land on the main menu (Command Center). Confirmed default (2026-02).
    navigate("/");
    onNavigate?.();
  };
  const go = (to) => {
    navigate(to);
    onNavigate?.();
  };

  // ─── Collapsed icon rail ────────────────────────────────────────────────
  if (collapsed) {
    const items = hub.sections.flatMap((s) => s.items).filter((it) => can(it.perm) || can("*"));
    return (
      <div className="flex h-full flex-col bg-shell-base" data-testid={`hubnav-${hub.key}`}>
        <button
          type="button"
          onClick={goBack}
          title="Back to Command Center"
          data-testid={`hubnav-${hub.key}-back`}
          className="flex items-center justify-center border-b border-shell-border py-3 text-shell-muted transition-colors hover:text-shell-text"
        >
          <ArrowLeft className="h-4 w-4" weight="bold" />
        </button>
        <nav className="flex-1 space-y-1 overflow-y-auto py-2" aria-label={`${hub.title} sections`}>
          {items.map((it) => {
            const on = it.key === activeKey;
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => go(it.to)}
                title={it.label}
                data-testid={`hubnav-${hub.key}-item-${it.key}`}
                className={cn(
                  "relative mx-2 flex items-center justify-center rounded-[2px] py-2.5 transition-colors",
                  on
                    ? "bg-card text-foreground ring-1 ring-border"
                    : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground"
                )}
              >
                <Icon className={cn("h-[18px] w-[18px]", on && "text-accent-ai")} weight={on ? "fill" : "bold"} />
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // ─── Expanded panel ─────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col bg-shell-base" data-testid={`hubnav-${hub.key}`}>
      {/* BACK — always to Command Center (/) */}
      <button
        type="button"
        onClick={goBack}
        data-testid={`hubnav-${hub.key}-back`}
        className="flex items-center gap-2 border-b border-shell-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-shell-muted transition-colors hover:text-shell-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" weight="bold" /> Back
      </button>

      {/* Hub identity */}
      <div className="border-b border-shell-border px-4 py-3">
        {hub.eyebrow && (
          <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-shell-muted">{hub.eyebrow}</p>
        )}
        <h2 className="font-heading text-base font-semibold tracking-tight text-sidebar-foreground" data-testid={`hubnav-${hub.key}-title`}>
          {hub.title}
        </h2>
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto py-2" aria-label={`${hub.title} sections`}>
        {hub.sections.map((section) => {
          const visible = section.items.filter((it) => can(it.perm) || can("*"));
          if (visible.length === 0) return null;
          return (
            <div key={section.key} className="pb-3">
              <p className="px-5 pb-1.5 pt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-shell-muted/70">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {visible.map((it) => {
                  const on = it.key === activeKey;
                  const Icon = it.icon;
                  const count = it.badge ? (badges?.[it.badge] || 0) : 0;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => go(it.to)}
                      aria-current={on ? "page" : undefined}
                      data-testid={`hubnav-${hub.key}-item-${it.key}`}
                      className={cn(
                        "group relative mx-2 flex w-[calc(100%-1rem)] items-center gap-2.5 rounded-[2px] px-3 py-2 text-left text-sm font-medium transition-colors",
                        on
                          ? "bg-card font-semibold text-foreground shadow-sm ring-1 ring-border"
                          : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground"
                      )}
                    >
                      {on && <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[3px] bg-accent-ai" />}
                      <Icon
                        className={cn(
                          "h-[18px] w-[18px] shrink-0 transition-colors",
                          on ? "text-accent-ai" : "text-sidebar-muted group-hover:text-sidebar-foreground"
                        )}
                        weight={on ? "fill" : "bold"}
                      />
                      <span className="truncate">{it.label}</span>
                      {!on && it.ai && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-ai/70" />}
                      <ItemBadge count={count} tone={it.badgeTone} />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default HubNavPanel;
